import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { VerdictBadge } from "../components/VerdictBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sparkles, History, Bookmark } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, refresh, loading } = useAuth();
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [saved, setSaved] = useState([]);
    const [loadingLists, setLoadingLists] = useState(true);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login", { state: { from: "/dashboard" } }); return; }
        (async () => {
            setLoadingLists(true);
            try {
                const h = await api.get("/factcheck/history/mine");
                setHistory(h.data);
                if (user.is_pro) {
                    const s = await api.get("/factcheck/saved/mine");
                    setSaved(s.data);
                }
            } catch (err) {
                if (process.env.NODE_ENV !== "production") console.debug("dashboard load error", err);
            }
            setLoadingLists(false);
        })();
    }, [user, loading, navigate]);

    useEffect(() => {
        // Poll payment after upgrade redirect
        const sid = searchParams.get("session_id");
        const upgrade = searchParams.get("upgrade");
        if (upgrade === "success" && sid) {
            let attempts = 0;
            const poll = async () => {
                if (attempts >= 6) return;
                attempts += 1;
                try {
                    const { data } = await api.get(`/payments/status/${sid}`);
                    if (data.payment_status === "paid") {
                        toast.success("You're Pro! Welcome to Real-Check Pro.");
                        await refresh();
                        return;
                    }
                    if (data.status === "expired") return;
                    setTimeout(poll, 2000);
                } catch {
                    setTimeout(poll, 2000);
                }
            };
            poll();
        }
    }, [searchParams, refresh]);

    if (!user) return null;

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                <div>
                    <p className="text-xs uppercase tracking-widest text-primary mb-2">Your dashboard</p>
                    <h1 className="font-display text-3xl md:text-4xl text-accent" data-testid="dashboard-welcome">
                        Hey {user.name?.split(" ")[0]},
                    </h1>
                    <p className="text-muted-foreground mt-2 text-sm">
                        {user.is_pro
                            ? "Pro member · unlimited checks · thanks for backing the mission."
                            : `Free tier · ${user.daily_checks_used}/${user.daily_limit} checks used today.`}
                    </p>
                </div>
                <div className="flex gap-3">
                    {!user.is_pro && (
                        <Button
                            onClick={() => navigate("/pricing")}
                            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                            data-testid="dashboard-upgrade-button"
                        >
                            <Sparkles className="h-4 w-4 mr-2" /> Upgrade to Pro
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => navigate("/")}
                        className="rounded-full"
                        data-testid="dashboard-new-check-button"
                    >
                        + New fact-check
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="history" className="w-full">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger value="history" data-testid="tab-history-trigger">
                        <History className="h-4 w-4 mr-2" /> History
                    </TabsTrigger>
                    <TabsTrigger value="saved" data-testid="tab-saved-trigger">
                        <Bookmark className="h-4 w-4 mr-2" /> Saved {user.is_pro ? "" : "(Pro)"}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="history" className="mt-8">
                    <ListSection items={history} empty="Your fact-check history will appear here." loading={loadingLists} testid="history-list" />
                </TabsContent>

                <TabsContent value="saved" className="mt-8">
                    {user.is_pro ? (
                        <ListSection items={saved} empty="Save any fact-check to keep it here." loading={loadingLists} testid="saved-list" />
                    ) : (
                        <Card className="card-surface p-10 rounded-2xl text-center">
                            <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
                            <h3 className="font-display text-2xl text-accent mb-2">Saved is a Pro feature</h3>
                            <p className="text-muted-foreground max-w-md mx-auto text-sm">
                                Upgrade to Pro for unlimited fact-checks, saved history, priority speed, and no ads.
                                Half of every Pro subscription funds Hands of Hope.
                            </p>
                            <Button
                                onClick={() => navigate("/pricing")}
                                className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                                data-testid="saved-upgrade-cta"
                            >
                                Upgrade to Pro
                            </Button>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function ListSection({ items, empty, loading, testid }) {
    if (loading) return <p className="text-muted-foreground text-sm">Loading…</p>;
    if (items.length === 0) return (
        <Card className="card-surface p-10 rounded-2xl text-center text-muted-foreground" data-testid={`${testid}-empty`}>
            {empty}
        </Card>
    );
    return (
        <div className="grid md:grid-cols-2 gap-4" data-testid={testid}>
            {items.map((tc, i) => (
                <Link
                    key={tc.check_id}
                    to={`/check/${tc.check_id}`}
                    className="card-surface rounded-2xl p-6 hover:-translate-y-0.5 transition-transform"
                    data-testid={`${testid}-item-${i}`}
                >
                    <VerdictBadge verdict={tc.verdict} confidence={tc.confidence} size="sm" />
                    <p className="mt-4 text-accent font-medium leading-snug line-clamp-3">&ldquo;{tc.claim}&rdquo;</p>
                    <p className="mt-2 text-xs text-muted-foreground">{new Date(tc.created_at).toLocaleString()}</p>
                </Link>
            ))}
        </div>
    );
}
